/**
 * Dashboard category summaries — the five cards across the Radar overview.
 *
 * PRESENTATION ONLY. These are a re-cut of tool scores the audit already
 * produced, grouped the way the sidebar has always grouped them. They do NOT
 * feed the primary Radar score, the projection, the roadmap, or anything
 * persisted. `aggregateToolScores` remains the single source of truth for the
 * headline number; nothing here is allowed to influence it.
 *
 * The grouping is the SAME map the dashboard sidebar renders, lifted out of
 * page.tsx so the nav and the cards can never drift apart.
 *
 * Withheld / failed / idle tools are EXCLUDED from the mean, never counted as
 * zero — the rule `lib/radar/tool-scores.ts` exists to enforce. A category
 * whose tools all withheld has no score, and renders as `--`.
 */

import { TOOLS } from '@/types/radar'
import type { ToolKey, ToolState } from '@/types/radar'
import { gradeFromScore } from './scoring'
import { hasUsableScore, usableScore } from './tool-scores'

export interface CategoryDef {
  /** Stable identifier, used for React keys and filter round-trips. */
  key: string
  /** Display label. Also the sidebar group heading. */
  label: string
  /** Tools in this category, in display order. */
  keys: readonly ToolKey[]
}

/**
 * The five dashboard categories, in display order.
 *
 * Every tool in TOOLS belongs to exactly one category — asserted by
 * `categories.test.ts`, so adding a 14th tool without categorising it fails
 * the suite rather than silently vanishing from the dashboard.
 */
export const CATEGORIES: readonly CategoryDef[] = [
  {
    key: 'crawlability',
    label: 'Crawlability',
    keys: ['crawl', 'robots', 'llms'],
  },
  {
    key: 'content',
    label: 'Content',
    keys: ['readiness', 'aeo', 'schemaAudit'],
  },
  {
    key: 'citations',
    label: 'Citations',
    keys: ['citation', 'answer', 'promptSov'],
  },
  {
    key: 'community',
    label: 'Community',
    keys: ['reddit', 'sourceInfluence'],
  },
  {
    key: 'trust',
    label: 'Trust',
    keys: ['hallucinationCheck', 'brandDisambiguation'],
  },
] as const

/** One tool's contribution to a category card. */
export interface CategoryMember {
  key: ToolKey
  name: string
  /** null when the tool withheld, failed, or has not run. Never coerced. */
  score: number | null
  /** null whenever `score` is null. */
  grade: string | null
  /** The tool completed but explicitly declined to grade. */
  withheld: boolean
}

export interface CategorySummary {
  key: string
  label: string
  /**
   * Mean of the usable member scores, or null when none were usable.
   * NEVER 0 as a stand-in for "no data".
   */
  score: number | null
  /** Grade for `score` via the shared band table, or null when unscored. */
  grade: string | null
  /** Members with a usable score — the denominator behind `score`. */
  scoredCount: number
  /** Every member, scored or not, in category order. Drives the meter rows. */
  members: CategoryMember[]
}

const TOOL_NAMES: Record<string, string> = Object.fromEntries(
  TOOLS.map(t => [t.key, t.name])
)

/**
 * Summarise one category from live tool state.
 *
 * Exported for the tests and for callers that need a single card; the
 * dashboard uses `summariseCategories`.
 */
export function summariseCategory(
  def: CategoryDef,
  tools: Partial<Record<ToolKey, ToolState>>
): CategorySummary {
  const members: CategoryMember[] = def.keys.map(key => {
    const state = tools[key]
    // usableScore() is the gate: it returns null for withheld, errored,
    // running and idle alike, so no branch here can leak a null into a sum.
    const score = usableScore(state)
    const data = state?.data as {
      scoreStatus?: unknown
      overallScore?: unknown
    } | null
    return {
      key,
      name: TOOL_NAMES[key] ?? key,
      score,
      grade: score === null ? null : (state?.data?.grade ?? null),
      withheld:
        state?.status === 'done' &&
        (data?.scoreStatus === 'withheld' || data?.overallScore === null),
    }
  })

  const scored = members.filter(
    (m): m is CategoryMember & { score: number } => m.score !== null
  )

  if (scored.length === 0) {
    return {
      key: def.key,
      label: def.label,
      score: null,
      grade: null,
      scoredCount: 0,
      members,
    }
  }

  const mean = Math.round(
    scored.reduce((sum, m) => sum + m.score, 0) / scored.length
  )

  return {
    key: def.key,
    label: def.label,
    score: mean,
    // The shared band table — categories never invent their own thresholds.
    grade: gradeFromScore(mean),
    scoredCount: scored.length,
    members,
  }
}

/** Summarise every category, in display order. */
export function summariseCategories(
  tools: Partial<Record<ToolKey, ToolState>>
): CategorySummary[] {
  return CATEGORIES.map(def => summariseCategory(def, tools))
}

/**
 * Accessible name for a control that filters the tool table to a category.
 *
 * Lives here because TWO components render that control — the marketing
 * `CategoryGrid` cards and the dashboard `SignalProfile` rows — and a
 * screen-reader user must hear the same sentence from either. Duplicating the
 * template in both is how the two drift.
 */
export function categoryControlLabel(summary: CategorySummary): string {
  return summary.score === null
    ? `${summary.label}: not scored. Show these tools.`
    : `${summary.label}: ${summary.score} out of 100, grade ${summary.grade}. Show these tools.`
}

/** The category a tool belongs to, or undefined if it is uncategorised. */
export function categoryForTool(key: ToolKey): CategoryDef | undefined {
  return CATEGORIES.find(c => (c.keys as readonly string[]).includes(key))
}

/** Re-exported so `hasUsableScore` consumers can stay on one import. */
export { hasUsableScore }
